import cx from 'classnames'
import Styles from './PropertyForm.module.scss'
import { Form, Formik } from 'formik'
import { Property, PropertyType, PropertyValue } from 'api/clients/PropertyClient'
import SingleSelect from './Types/SingleSelect'
import Document from './Types/Document'
import Link from './Types/Link'
import Person from './Types/Person'
import Date from './Types/Date'
import Checkbox from './Types/Checkbox'
import NameInput from './NameInput'
import TypeSelect from './TypeSelect'
import FormButtons from './FormButtons'
import Text from './Types/Text'
import Number, { NumberFormat } from './Types/Number'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { Response } from 'request-dot-js'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { dismissToastMessages } from 'common/ToastMessage'
import { FormikCheckbox } from 'components/Form/Checkbox/Checkbox'
import { DateFormat } from 'common/PropertyForm/Types/Date'
import MultiSelect from './Types/MultiSelect'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { usePropertiesPermissions } from 'hooks/usePropertiesPermissions'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { isCalendarProperty } from 'screens/Finder/components/views/CalendarView/utils'

export interface Values {
  type: PropertyType
  name: string
  property_values: PropertyValue[]
  meta: {
    number_format?: NumberFormat
    date_format?: DateFormat
    allow_mentioning_multiple_people?: boolean
    notify_person?: boolean
  }
}

interface Props {
  variant?: 'large' | 'small'
  isRename?: boolean
  property?: Property
  type: PropertyType
  propertyTypes: any[]
  onCreate?: (property: Property) => Promise<void> | void
  onUpdate?: (property: Property, isUndoAction?: boolean) => Promise<Response<{ property: Property }>>
  onRemove?: (property: Property) => Promise<void> | void
  onSetType?: (type: PropertyType) => void
  // This property is only used for styling purposes. We should migrate all the
  // dropdowns to use the DropdownTrigger and deprecate this.
  allowEditType?: boolean
  removeText?: string
  showAddToDefault?: boolean
  disabled?: boolean
  workspaceProperty?: boolean
  forceShowNameInput?: boolean
  forceShowTypeSelector?: boolean
  openPropertyActions?: (propertyId: number | undefined) => void
  className?: string
}

interface TypeActionsProps {
  values: Values
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  isSubmitting: boolean
  propertyTypeChanged: boolean
  isValid: boolean
}

interface UndoToastProps {
  previousState: Property
  onClick: (property: Property) => void
}

const UndoMessageDuration = 5

export const showUndoMessageToast = ({ previousState, onClick }: UndoToastProps) => {
  showToastMessage(
    'Property type changed!',
    'success',
    {
      onPress: () => {
        onClick(previousState as Property)
        dismissToastMessages()
      },
      variant: 'cancel',
      label: 'Undo',
      type: 'button',
    },
    UndoMessageDuration,
  )
}

const propertyTypeToName = (type: PropertyType): string => {
  const mappings = {
    [PropertyType.Text]: 'Text',
    [PropertyType.SingleSelect]: 'Single Select',
    [PropertyType.MultiSelect]: 'Multi Select',
    [PropertyType.Date]: 'Date',
    [PropertyType.Document]: 'Document',
    [PropertyType.Person]: 'Person',
    [PropertyType.Checkbox]: 'Checkbox',
    [PropertyType.Number]: 'Number',
    [PropertyType.Link]: 'Link',
    [PropertyType.Title]: 'Name',
  } as { [key in PropertyType]: string }

  return mappings[type]
}

export const PropertyForm = ({
  variant = 'small',
  isRename = false,
  type,
  onSetType,
  propertyTypes,
  property,
  onCreate,
  onUpdate,
  onRemove,
  workspaceProperty,
  allowEditType = true,
  removeText = 'Remove',
  showAddToDefault = false,
  disabled = false,
  forceShowNameInput = false,
  forceShowTypeSelector = false,
  openPropertyActions,
  className,
}: Props) => {
  const isEdit = !!property?.id
  const { currentFolder } = useCurrentFolderStore()
  const { filesView } = useFinderContext()
  const currentProjectKey = currentFolder?.id

  const scopeProjectId = () => {
    if (!!workspaceProperty) {
      return null
    }
    return !isEdit ? currentProjectKey : property?.scope_project_id
  }

  const initialValues = {
    id: property?.id || '',
    name: property?.name || propertyTypeToName(type),
    type: type,
    meta: { ...property?.meta },
    property_values: property?.property_values || [],
    updated_at: property?.updated_at,
    scope_project_id: scopeProjectId(),
  }

  const onSubmitForm = isEdit ? onUpdate : onCreate

  const { canCreate, canEdit, canRemove } = usePropertiesPermissions(property)
  const canAct = isEdit ? canEdit : canCreate

  const propertyTypeChanged = !!(isEdit && property && property.type !== type)

  const handleOnSubmit = async (data: Values) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const response = await onSubmitForm!(data as Property)

    if (response && response.type === SUCCESS_RESPONSE_TYPE && propertyTypeChanged) {
      showUndoMessageToast({
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        previousState: property!,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        onClick: (data: Property) => onUpdate!(data, true),
      })
    }
  }

  const renderTypeActions = ({
    values,
    setFieldValue,
    isSubmitting,
    propertyTypeChanged,
    isValid,
  }: TypeActionsProps) => {
    switch (values.type) {
      case PropertyType.SingleSelect:
        return (
          <SingleSelect
            values={values}
            setFieldValue={setFieldValue}
            isSubmitting={isSubmitting}
            propertyTypeChanged={propertyTypeChanged}
            isValid={isValid}
          />
        )
      case PropertyType.MultiSelect:
        return (
          <MultiSelect
            values={values}
            setFieldValue={setFieldValue}
            isSubmitting={isSubmitting}
            propertyTypeChanged={propertyTypeChanged}
            isValid={isValid}
          />
        )
      case PropertyType.Document:
        return <Document />
      case PropertyType.Text:
        return <Text isValid={isValid} />
      case PropertyType.Person:
        return <Person setFieldValue={setFieldValue} values={values} isEdit={isEdit} isValid={isValid} />
      case PropertyType.Link:
        return <Link />
      case PropertyType.Number:
        return <Number isEdit={isEdit} isValid={isValid} setFieldValue={setFieldValue} values={values} />
      case PropertyType.Date:
        return <Date isEdit={isEdit} setFieldValue={setFieldValue} values={values} />
      case PropertyType.Checkbox:
        return <Checkbox />
    }
  }

  if (isCalendarProperty({ filesView, propertyId: property?.id }))
    return (
      <Formik initialValues={initialValues} onSubmit={(data: Values) => handleOnSubmit(data)} validateOnChange>
        {({ isSubmitting, isValid }) => (
          <Form
            className={cx(Styles.form, className, {
              [Styles.large]: variant === 'large',
            })}
          >
            <div>
              {(canAct || forceShowNameInput) && (
                <NameInput
                  autoFocus={true}
                  property={property}
                  isEdit={isEdit}
                  isRename={isRename}
                  disabled={disabled || !canAct}
                />
              )}
              {!disabled && (canAct || canRemove) && (
                <FormButtons
                  property={property}
                  isEdit={isEdit}
                  onCreate={canAct ? onCreate : undefined}
                  isSubmitting={isSubmitting}
                  isValid={isValid}
                  onUpdate={canAct ? onUpdate : undefined}
                  removeText=""
                />
              )}
            </div>
          </Form>
        )}
      </Formik>
    )

  return (
    <Formik initialValues={initialValues} onSubmit={(data: Values) => handleOnSubmit(data)} validateOnChange>
      {({ errors, isSubmitting, isValid, touched, values, setFieldValue }) => (
        <Form
          className={cx(Styles.form, className, {
            [Styles.large]: variant === 'large',
          })}
        >
          <div>
            {(canAct || forceShowNameInput) && (
              <NameInput
                autoFocus={true}
                property={property}
                isEdit={isEdit}
                isRename={isRename}
                disabled={disabled || !canAct}
              />
            )}
            {(canAct || forceShowTypeSelector) && (
              <TypeSelect
                errors={errors}
                touched={touched}
                type={values.type}
                propertyTypes={propertyTypes}
                onSelectType={(index: number) => {
                  setFieldValue(`type`, propertyTypes[index].type)
                  onSetType && onSetType(propertyTypes[index].type)
                }}
                setFieldValue={setFieldValue}
                isEdit={isEdit}
                disabled={disabled || !canAct || (isEdit && !allowEditType)}
              />
            )}
            {!disabled &&
              canAct &&
              renderTypeActions({ values, setFieldValue, isSubmitting, propertyTypeChanged, isValid })}
            {!disabled && canAct && showAddToDefault && !isEdit && (
              <FormikCheckbox
                onChange={(checked) => {
                  setFieldValue('default_for_articles', checked)
                }}
                size="md"
                name="default_for_articles"
                variant="grey"
                className={Styles.checkbox}
              >
                Show by default
              </FormikCheckbox>
            )}
            {!disabled && (canAct || canRemove) && (
              <FormButtons
                property={property}
                isEdit={isEdit}
                onCreate={canAct ? onCreate : undefined}
                isSubmitting={isSubmitting}
                isValid={isValid}
                onRemove={canRemove ? onRemove : undefined}
                onUpdate={canAct ? onUpdate : undefined}
                removeText={removeText}
              />
            )}
          </div>
        </Form>
      )}
    </Formik>
  )
}
